import React, { useRef, useState, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Type, Hash, ListOrdered, CheckCircle, SlidersHorizontal, Mail, FileUp, Image, HelpCircle } from 'lucide-react';
import './NodeStyles.css';

const QuestionNode = ({ id, data, selected }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftText, setDraftText] = useState(data.text || '');
  const textareaRef = useRef(null);
  const isFile = data.type === 'file';
  // Node-kind tag shown in the header (matches the redesign's per-type identity).
  const kindLabel = isFile ? 'FILE UPLOAD' : 'QUESTION';
  const getQuestionTypeIcon = (type) => {
    const size = 15;
    const sw = 2.1;
    switch (type) {
      case 'text':
        return <Type size={size} strokeWidth={sw} />;
      case 'number':
        return <Hash size={size} strokeWidth={sw} />;
      case 'choice':
        return <ListOrdered size={size} strokeWidth={sw} />;
      case 'yes_no':
        return <CheckCircle size={size} strokeWidth={sw} />;
      case 'scale':
        return <SlidersHorizontal size={size} strokeWidth={sw} />;
      case 'email':
        return <Mail size={size} strokeWidth={sw} />;
      case 'file':
        return <FileUp size={size} strokeWidth={sw} />;
      case 'media':
        return <Image size={size} strokeWidth={sw} />;
      default:
        return <HelpCircle size={size} strokeWidth={sw} />;
    }
  };

  const truncateText = (text, maxLength = 40) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(draftText.length, draftText.length);
    }
  }, [isEditing, draftText]);

  const commitEdit = () => {
    const newText = draftText.trim();
    // Dispatch update to app to persist
    const evt = new CustomEvent('graph-node-text-update', { detail: { nodeId: id, text: newText } });
    window.dispatchEvent(evt);
    setIsEditing(false);
  };

  const cancelEdit = () => {
    setDraftText(data.text || '');
    setIsEditing(false);
  };

  const handleDoubleClick = (e) => {
    e.stopPropagation();
    setDraftText(data.text || '');
    setIsEditing(true);
  };

  const onKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    }
  };

  return (
    <div className={`question-node ${isFile ? 'question-node--file' : ''} ${selected ? 'selected' : ''}`} onDoubleClick={handleDoubleClick} title={data.wfTooltip}>
      <Handle
        type="target"
        position={Position.Top}
        className="node-handle node-handle-top"
      />

      <div className="node-header">
        <div className="node-icon">
          {getQuestionTypeIcon(data.type)}
        </div>
        <span className="node-kind">{kindLabel}</span>
        <span className="node-ref" title={data.id}>{data.id}</span>
        {data.required && (
          <span className="required-indicator" title="Required question">
            *
          </span>
        )}
      </div>

      <div className="node-content">
        <div className="question-text" title={data.text}>
          {isEditing ? (
            <textarea
              ref={textareaRef}
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              onKeyDown={onKeyDown}
              onBlur={commitEdit}
              style={{
                width: '100%',
                resize: 'none',
                border: '1px solid #ddd',
                borderRadius: 4,
                font: 'inherit',
                padding: 6,
              }}
              rows={Math.min(4, Math.max(2, Math.ceil((draftText.length || 1) / 40)))}
            />
          ) : (
            truncateText(data.text)
          )}
        </div>

        {data.type === 'choice' && data.choices && data.choices.length > 0 && (
          <div className="choices-preview">
            {data.choices.slice(0, 2).map((choice, index) => (
              <span key={index} className="choice-item">
                {choice}
              </span>
            ))}
            {data.choices.length > 2 && (
              <span className="choice-more">
                +{data.choices.length - 2} more
              </span>
            )}
          </div>
        )}

        {(data.type === 'number' || data.type === 'scale') && (data.minValue !== null || data.maxValue !== null) && (
          <div className="range-preview">
            Range: {data.minValue || '∞'} - {data.maxValue || '∞'}
          </div>
        )}

        {data.condition && (
          <div className="condition-preview" title={`Condition: ${data.condition}`}>
            🔗 Conditional
          </div>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="node-handle node-handle-bottom"
      />
    </div>
  );
};

export default QuestionNode; 